import {
  PermissionCategory,
  PermissionLevel,
  SCOPED_CATEGORIES,
} from '@/client/custom-roles';
import { LoadingStatuses } from '@/common/constants';
import { CustomRoleFormValues } from '@/components/custom-roles/forms/validations/custom-roles';
import { AppButton } from '@/ui/buttons';
import { PERMISSION_CATEGORY_ORDER } from '@/utils/custom-roles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

type RoleSummaryCardProps = {
  values: CustomRoleFormValues;
  assignedUserCount: number;
  isSaving: boolean;
  onSave: () => void;
  onCancel: () => void;
};

const Card = styled.aside`
  display: flex;
  flex-direction: column;
  gap: var(--medium-big-padding); /* Figma 28px -> 30px */
  width: 273px;
  flex-shrink: 0;
  padding: var(--default-padding-small);
  background-color: var(--white-main);
  border: 1px solid var(--gray-border-lightest);
  border-radius: var(--xsmall-border-radius);
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xxsmall-padding);
`;

const Title = styled.span`
  font-size: var(--small-font-size);
  font-weight: var(--semibold-font-weight);
  line-height: var(--small-line-height);
`;

const Subtitle = styled.span`
  font-size: var(--xsmall-font-size);
  font-weight: var(--regular-font-weight);
  line-height: var(--xsmall-line-height);
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--small-padding); /* Figma 11px -> --small-padding (12px) */
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xsmall-padding);
`;

const Rule = styled.hr`
  width: 100%;
  margin: 0;
  border: none;
  border-top: 1px solid var(--gray-border-lightest);
`;

const ItemText = styled.span`
  font-size: var(--xsmall-font-size);
  font-weight: var(--regular-font-weight);
  color: var(--purple-dark);
`;

const Value = styled.span`
  font-weight: var(--bold-font-weight);
`;

const Assigned = styled.span`
  display: flex;
  align-items: center;
  gap: var(--semi-xsmall-padding);
  font-size: var(--xsmall-font-size);
  font-weight: var(--regular-font-weight);
  color: var(--gray-medium-dark);
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  gap: var(--medium-padding); /* Figma 15px -> --medium-padding (16px) */
`;

export const RoleSummaryCard: React.FC<RoleSummaryCardProps> = ({
  values,
  assignedUserCount,
  isSaving,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();

  const levelLabel = (level: PermissionLevel) =>
    level === PermissionLevel.NONE
      ? t('account.rolesAccess.builder.summary.noAccess')
      : t(`account.rolesAccess.level.${level}`);

  const scopeSuffix = (category: PermissionCategory) =>
    SCOPED_CATEGORIES.includes(category) &&
    values.permissions[category] !== PermissionLevel.NONE &&
    values.scopeMode
      ? ` (${t('account.rolesAccess.builder.summary.visibility', {
          mode: t(`account.rolesAccess.scopeMode.short.${values.scopeMode}`),
        })})`
      : '';

  return (
    <Card>
      <Header>
        <Title>
          {t('account.rolesAccess.builder.summary.title', {
            name:
              values.name || t('account.rolesAccess.builder.summary.unnamed'),
          })}
        </Title>
        <Subtitle>{t('account.rolesAccess.builder.summary.subtitle')}</Subtitle>
      </Header>

      <List>
        {PERMISSION_CATEGORY_ORDER.map((category, index) => (
          <Item key={category}>
            {index > 0 && <Rule />}
            <ItemText>
              {t(`account.rolesAccess.category.${category}.title`)}:{' '}
              <Value>{levelLabel(values.permissions[category])}</Value>
              {scopeSuffix(category)}
            </ItemText>
          </Item>
        ))}
      </List>

      <Assigned>
        <i className="pi pi-user-plus" />
        {t('account.rolesAccess.builder.summary.assigned', {
          count: assignedUserCount,
        })}
      </Assigned>

      <Buttons>
        <AppButton
          label={t('account.rolesAccess.builder.save')}
          onClick={onSave}
          state={isSaving ? LoadingStatuses.LOADING : LoadingStatuses.IDLE}
        />
        <AppButton label={t('cancel')} type="outlined" onClick={onCancel} />
      </Buttons>
    </Card>
  );
};
